import { createContext, useContext, useEffect, useRef, useState } from "react";
import { setLogoutFunction } from "services/api";

const AuthContext = createContext();

// Auto-logout after this much inactivity.
const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!sessionStorage.getItem("token"));
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => sessionStorage.getItem("token"));
  const idleTimerRef = useRef(null);

  useEffect(() => {
    setLogoutFunction(logout);
  }, []);

  // Log the user out automatically after IDLE_TIMEOUT_MS of no activity.
  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const resetTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        logout();
      }, IDLE_TIMEOUT_MS);
    };

    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];
    events.forEach((event) => window.addEventListener(event, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      events.forEach((event) => window.removeEventListener(event, resetTimer));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const login = (credentials, tokenValue) => {
    setUser(credentials);
    setToken(tokenValue);
    setIsAuthenticated(true);
    sessionStorage.setItem("token", tokenValue); // persist login
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    sessionStorage.removeItem("token");
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, token, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
